import React, { useEffect, useState } from "react";
import { primaryColor } from "../services/theme_service";
import closeIcon from "../assets/images/close.png";
import { getAddressList } from "../services/api_service";
import { Address } from "../models/address_model";
import { useMediaQuery } from "react-responsive";

type SelectAddressProps = {
  open: boolean;
  onClose?: () => void;
  onSelect?: (address: Address) => void;
  selectedAddress?: Address | null;
};

const SelectAddress: React.FC<SelectAddressProps> = ({
  open,
  onClose,
  onSelect,
  selectedAddress = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(open);
  const [animate, setAnimate] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const userId = localStorage.getItem("userId");

  // Fetch addresses
  const fetchAddresses = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const list = await getAddressList(userId);
      setAddresses(list || []);
    } catch (e) {
      console.error("Failed to fetch addresses", e);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal open/close + animation
  useEffect(() => {
    if (open) {
      setShow(true);
      requestAnimationFrame(() => setAnimate(true));
      if (userId) fetchAddresses();
    } else {
      setAnimate(false);
      setTimeout(() => setShow(false), 300);
    }
  }, [open, userId]);

  if (!show) return null;

  return (
    <div>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: "100vw",
          background: "rgba(0,0,0,0.3)",
          zIndex: 2000,
          opacity: animate ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          background: "#fff",
          zIndex: 2001,
          padding: 20,
          boxShadow: isMobile
            ? "0 -2px 10px rgba(0,0,0,0.2)"
            : "-2px 0 10px rgba(0,0,0,0.2)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          ...(isMobile
            ? {
                bottom: 0,
                left: 0,
                width: "100vw",
                height: "85dvh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                transform: animate ? "translateY(0)" : "translateY(100%)",
                transition: "transform 0.3s ease-in-out",
                willChange: "transform",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }
            : {
                top: 0,
                right: 0,
                width: "35%",
                height: "100dvh",
                transform: animate ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.3s ease-in-out",
                willChange: "transform",
                overflowY: "auto",
              }),
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10,  position: "sticky" }}>
          <img
            src={closeIcon}
            alt="close"
            style={{ width: 24, height: 24, cursor: "pointer" }}
            onClick={onClose}
          />
          <span style={{ fontSize: 18, fontWeight: 600, flex: 1, marginLeft: 15 }}>
            Select Address
          </span>
        </div>

        {/* Body */}
        <div style={{ display: "flex", flexDirection: "column", gap: 15, flex: 1, marginTop: 10, overflowY: "auto" }}>
          {loading ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 150,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "4px solid #f3f3f3",
                  borderTop: `4px solid ${primaryColor}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          ) : addresses.length === 0 ? (
            <div style={{ textAlign: "center", color: "#888" }}>No addresses found</div>
          ) : (
            addresses.map((addr) => {
              const isSelected = selectedAddress?.address_id === addr.address_id;
              return (
                <div
                  key={addr.address_id}
                  onClick={() => onSelect?.(addr)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: "#FFF7EB",
                    borderRadius: 12,
                    padding: 15,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    border: isSelected ? `2px solid ${primaryColor}` : "2px solid transparent",
                  }}
                >
                  {/* Top row: name + icons */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>🏠</span>
                      <span style={{ fontWeight: 600 }}>
                        {addr.first_name} {addr.last_name}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <span
                        style={{
                          padding: 4,
                          borderRadius: 6,
                          backgroundColor: "#eee",
                          cursor: "pointer",
                        }}
                      >
                        ✏️
                      </span>
                      <span
                        style={{
                          padding: 4,
                          borderRadius: 6,
                          backgroundColor: "#eee",
                          cursor: "pointer",
                        }}
                      >
                        🗑️
                      </span>
                    </div>
                  </div>

                  {/* Address */}
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: 8,
                      display: "flex",
                      flexDirection: "column",
                      padding: 15,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{addr.address1}</span>
                      {addr.address2 && (
                        <span style={{ fontSize: 14, color: "#555" }}>{addr.address2}</span>
                      )}
                      <span style={{ fontSize: 15, color: "#555" }}>
                        {addr.city}, {addr.state} - {addr.zip_code}
                      </span>
                      <span style={{ fontSize: 15, color: "#555" }}>{addr.country}</span>
                    </div>

                    {/* Email & Phone */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 10 }}>
                      {addr.email && <span>📧 {addr.email}</span>}
                      {addr.phone_number && <span>📞 {addr.phone_number}</span>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
};

export default SelectAddress;
